
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useSelector } from "umi"
import moment from "moment"
import CardBox from "@/pages/dashboard/components/CardBox"
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart"
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart"
import { addChartLabel, xAxisHighLight } from "@/pages/dashboard/components/Chart/_config/plug"
import { getEchartTooltipText } from "@/pages/dashboard/components/EchartsBubble"
import MiniTable from "@/pages/dashboard/components/MiniTable"
import { useLocal, useOption, useWeekDay } from "@/pages/dashboard/utils/customHook"
import { formatPercentNum, handleDownload, formatFilterValues, commonValueFormatter, commonDownloadValueFormatter } from "@/pages/dashboard/utils/utils"
import { momentFormat } from "@/pages/dashboard/utils/_config/filtersTimeFormat"
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context"
import { getApiData } from "@/pages/dashboard/services/common"

const tabNames: any = {
    Day: 'daily',
    Week: 'weekly',
    Month: 'monthly'
}
const ReturnTrendCo = (props: any) => {
    const { config: propsConfig, className, style } = props
    const { api, componentMetrics } = propsConfig || {}
    const { gameCode } = useSelector((state: any) => {
        return {
            gameCode: state?.dashboard?.currentGame?.game_code,
        }
    });
    const { getWeekDay } = useWeekDay()
    const { isEN, locale, formatMessage } = useLocal()
    const contextObj = useContext(FilterContext)
    const { filterVal, filterLoading } = contextObj || {};
    const { timeType = 'Day' } = filterVal || {}
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [trendData, setTrendData] = useState([])
    const [iseye, setIseye] = useState(true)

    const { title, nameMap } = useMemo(() => {
        const { enTitle, cnTitle, enWeekTitle, cnWeekTitle, enMonthTitle, cnMonthTitle } = propsConfig || {}
        const titleText: any = {
            Day: isEN ? enTitle : cnTitle,
            Week: isEN ? enWeekTitle : cnWeekTitle,
            Month: isEN ? enMonthTitle : cnMonthTitle,
        }
        const nameMap: any = {
            Day: componentMetrics?.map((item: any) => isEN ? item?.enName : item?.cnName) || [],
            Week: componentMetrics?.map((item: any) => isEN ? item?.enWeekName : item?.cnWeekName) || [],
            Month: componentMetrics?.map((item: any) => isEN ? item?.enMonthName : item?.cnMonthName) || []
        }
        return {
            title: titleText?.[timeType],
            nameMap,
        }
    }, [isEN, timeType, propsConfig, componentMetrics])

    useEffect(() => {
        if (filterLoading) return
        getTrendData()
    }, [filterVal, filterLoading])

    const getTrendData = () => {
        const { timeType = 'Day', diff, rangeTime, ...filters } = filterVal || {}
        const params = {
            period: tabNames[timeType],
            game_code: gameCode,
            start_time: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
            end_time: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
            filters: formatFilterValues(filters),
        }
        getApiData(api, params).then(res => {
            setTrendData(res?.nodes || [])
            setLoading(false)
            setError(false)
        }).catch(err => {
            setTrendData([])
            setLoading(false)
            setError(true)
        })
    }

    const handleAxisFormat = (value) => {
        return timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD')
    }

    /**
     * 获取Options
     * @param dataList 数据
     * @param setTableHeader 表格表头
     * @param nowKey 当前日期key
     * @param preCycleKey 上个周期的key
     * @returns echarts初始化option
     */
    const getOptions = (dataList: any = [], setTableHeader: {}[], nowKey: string = 'this_value', preCycleKey: string = 'pre_cycle_value') => {
        let options = {
            chart: [] as Array<{}>,
            table: {
                option: [] as Array<{}>,
                data: [] as Array<{}>,
                downloadData: [] as Array<{}>,
            }
        }
        const legend = [{ key: nowKey, name: nameMap[timeType][0] }, { key: preCycleKey, name: nameMap[timeType][1] }]
        if (dataList === null) return options;
        let chartData: Array<{}> = []
        let tableHeader: Array<{}> = setTableHeader
        let tableData: Array<{}> = []
        let downloadData: Array<{}> = []
        legend.forEach((ele, index) => {
            let chartConfig = {
                name: ele.name,
                type: 'bar',
                data: [] as Array<{}>
            }
            dataList.forEach((row: { [x: string]: any; date: any }) => {
                let date = handleAxisFormat(row.date)
                chartConfig.data.push({
                    x: date,
                    y: Math.round(row[ele.key]) || row[ele.key]
                })
            })
            chartConfig.data.length && chartData.push(chartConfig)
        })
        dataList.forEach((row: { [x: string]: any; date: any }) => {
            let date: any = timeType === 'Month' ? momentFormat(row.date, 'YYYY-MM') : row.date
            let temp: any = { date: date }
            let downloadTemp: any = { date: date }
            componentMetrics.forEach((item: any) => {
                const { metricKey, type, decimal } = item || {}
                const value = row?.[metricKey] || ''
                temp[metricKey] = commonValueFormatter(type, value, decimal)
                downloadTemp[metricKey] = commonDownloadValueFormatter(type, value, decimal)
            })
            tableData.push(temp)
            downloadData.push(downloadTemp)
        })
        options = {
            chart: chartData,
            isCompare: true,
            table: {
                option: tableHeader,
                data: tableData,
                downloadData
            }
        }
        return options
    }

    const returnUsersOption = useMemo(() => {
        const usersCountTable = [
            {
                key: 'date',
                header: formatMessage({ id: 'return_data' }),
                align: 'left',
                fixed: 'left',
                width: 100,
                render: (cvm) => {
                    const weekDay = getWeekDay(cvm?.date)
                    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六'
                    return <>
                        <span className='table-text'>{cvm?.date}</span>
                        {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
                    </>
                },
            }, {
                key: 'this_value',
                header: nameMap?.[timeType]?.[0] || '',
                align: 'right',
                width: 100,
                render: (tr: any) => (<span>{tr.this_value}</span>),
            },
            {
                key: 'pre_cycle_value',
                header: nameMap?.[timeType]?.[1] === 'Same Day Last Week' ? 'SDLW' : nameMap?.[timeType]?.[1] || '',
                align: 'right',
                width: 100,
                render: (tr: any) => (<span>{tr.pre_cycle_value}</span>),
            },
        ];
        return getOptions(trendData ?? [], usersCountTable)
    }, [locale, trendData, filterVal, nameMap])

    /**
       * 处理option为组件需要的格式
       * @params options
       */
    const handleConfig = useCallback((options) => {
        function formatterFunc(param: any) {
            let data: Array<{}> = []
            for (let i = 0; i < param.length; i++) {
                let { color, seriesName, name } = param[i];
                const { type, decimal } = componentMetrics?.[i] || {}
                let value = commonValueFormatter(type, param[i].value, decimal)
                const targetItem = trendData?.find(item => item?.date?.includes(name));
                const num = Number(seriesName === nameMap[timeType][0] ? targetItem?.this_xox : targetItem?.pre_cycle_xox);
                const status = (() => {
                    if (num === 0) return 'zero'
                    if (num > 0) return 'up'
                    if (num < 0) return 'down'
                    return 'zero'
                })()
                const ratioNumPercent = formatPercentNum(num, 1, true)

                data.push({
                    name: seriesName === 'Same Day Last Week' ? 'SDLW' : seriesName,
                    metricsNum: value,
                    ratioStatus: status,
                    ratioNum: `${ratioNumPercent}`,
                    color
                })
            }
            let temp = {
                title: param[0].name,
                metricsName: '',
                ratioName: '',
                hasRatio: true,
                data: data
            }
            return getEchartTooltipText(temp)
        }
        function setOtherConfigFunc(opt) {
            opt?.series?.forEach((item, index) => {
                item.z = index === 0 ? 1 : 0
                if (index === 1) {
                    item.itemStyle = {
                        normal: {
                            lineStyle: {
                                width: 2,
                                type: 'dotted'
                            }
                        }
                    }
                }
            })
            const { type, decimal } = componentMetrics?.[0] || {}
            opt = addChartLabel(opt, {
                formatter: (params: { value: string | number; }) => {
                    return commonValueFormatter(type, params?.value, decimal)
                },
                show: iseye,
                formatNum: (value: string | number) => {
                    return commonValueFormatter(type, value, decimal)
                }
            })
            opt = xAxisHighLight(opt, {
                formatter: (params: { value: string; }) => {
                    return handleAxisFormat(params?.value)
                },
            })
            return opt;
        }
        return {
            bar: {
                formatterFunc,
                setOtherConfigFunc
            },
            line: {
                formatterFunc,
                setOtherConfigFunc
            },
            table: {
                isSortable: true,
                setSortableConfig: (sorts, setSorts) => {
                    return {
                        columns: options.table.option.map((e: any) => {
                            return {
                                key: e.key,
                                prefer: 'desc',
                                sorter: (f, s) => f[e.key].replace(/[,-]/g, '') - s[e.key].replace(/[,-]/g, ''),
                            }
                        }),
                        value: sorts,
                        onChange: (value, context) => {
                            setSorts([context?.sort])
                        },
                    }
                }
            }
        }
    }, [trendData, filterVal, iseye])

    const config = handleConfig(returnUsersOption)
    const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(returnUsersOption)
    const renderList = [
        {
            type: 'eyes',
            svgType: iseye ? 'open-eyes' : 'eyes-closed',
            content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
            isHideCom: true,
        },
        {
            type: 'barChart',
            svgType: 'column-chart',
            content: formatMessage({ id: 'common_bar_chart' }),
            component: <BarChart
                height={340}
                legendData={xAxisData.length > 0 ? legendData : []}
                xAxisData={xAxisData || []}
                series={xAxisData.length > 0 ? barSeries : []}
                formatterFunc={config?.bar?.formatterFunc}
                setOtherConfigFunc={(opt: any) => {
                    if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt)
                    return opt
                }}
            />
        },
        {
            type: 'lineChart',
            svgType: 'discount-chart',
            content: formatMessage({ id: 'common_line_chart' }),
            component: <LineChart
                height={340}
                legendData={xAxisData.length > 0 ? legendData : []}
                xAxisData={xAxisData || []}
                series={xAxisData.length > 0 ? lineSeries : []}
                formatterFunc={config?.line?.formatterFunc}
                setOtherConfigFunc={(opt: any) => {
                    if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt)
                    return opt;
                }}
            />
        },
        {
            type: 'sheet',
            svgType: 'sheet',
            content: formatMessage({ id: 'common_table' }),
            component: <MiniTable
                height={340}
                loading={false}
                tableConfig={config?.table}
                tableOption={returnUsersOption?.table?.option}
                tableList={returnUsersOption?.table?.data}
            />
        },
    ]

    return <CardBox
        className={`register-table ${className ? className : ''}`}
        height={340}
        bordered={false}
        titleCom={title}
        renderList={renderList}
        defaultAvtive={2}
        setCurrentDataType={(type: any) => {
            if (type === 'eyes') {
                setIseye(!iseye)
            }
        }}
        downloadClick={async () => {
            handleDownload(returnUsersOption?.table, title)
        }}
        isLoading={loading}
        isError={error}
        isNodata={isNodata}
    />
}

export default ReturnTrendCo